import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { Button } from "@/components/ui";
import { Mail, Lock, AlertCircle, ArrowRight } from "lucide-react";
import VerifyEmailPage from "./VerifyEmailPage";
import InviteBanner from "@/components/auth/InviteBanner";
import { isSafeRedirect } from "@/lib/utils";

const loginSchema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(6, "Mínimo 6 caracteres"),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const {
    signIn,
    error,
    clearError,
    emailNotVerified,
    user,
    needsSetup,
    pendingApproval,
  } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [attemptedEmail, setAttemptedEmail] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const decodedRedirect = redirectParam
    ? decodeURIComponent(redirectParam)
    : "";
  const redirectAfterLogin = isSafeRedirect(decodedRedirect)
    ? decodedRedirect
    : "/dashboard";

  // useForm debe llamarse ANTES de cualquier return condicional (Rules of Hooks)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  // Si ya está autenticado, redirigir al destino
  if (user) {
    if (needsSetup)
      return (
        <Navigate
          to={
            redirectAfterLogin !== "/dashboard" ? redirectAfterLogin : "/setup"
          }
          replace
        />
      );
    if (pendingApproval) return <Navigate to="/pending-approval" replace />;
    return <Navigate to={redirectAfterLogin} replace />;
  }

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    clearError();
    setAttemptedEmail(data.email);

    try {
      await signIn(data.email, data.password);
      navigate(redirectAfterLogin, { replace: true });
    } catch (err) {
      if (err instanceof Error && err.message === "EMAIL_NOT_VERIFIED") {
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  // Mostrar página de verificación de email si no está verificado
  if (emailNotVerified && attemptedEmail) {
    return (
      <VerifyEmailPage email={attemptedEmail} redirect={redirectAfterLogin} />
    );
  }

  return (
    <div>
      <InviteBanner />
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">
          ¡Bienvenido de vuelta!
        </h2>
        <p className="text-gray-500 mt-2">
          Ingresa tu correo electrónico para acceder
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Correo electrónico
          </label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              autoComplete="email"
              placeholder="tu@correo.com"
              className={`w-full pl-12 pr-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-500/20 ${
                errors.email
                  ? "border-red-300 focus:border-red-500"
                  : "border-gray-200 focus:border-primary-500 hover:border-gray-300"
              }`}
              {...register("email")}
            />
          </div>
          {errors.email && (
            <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Contraseña
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              className={`w-full pl-12 pr-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-500/20 ${
                errors.password
                  ? "border-red-300 focus:border-red-500"
                  : "border-gray-200 focus:border-primary-500 hover:border-gray-300"
              }`}
              {...register("password")}
            />
          </div>
          {errors.password && (
            <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.password.message}
            </p>
          )}
        </div>

        {error && (
          <div className="p-4 rounded-xl bg-gradient-to-r from-red-50 to-red-100/50 border-2 border-red-100 text-red-700 text-sm flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
              <AlertCircle className="w-4 h-4 text-white" />
            </div>
            <span>{error}</span>
          </div>
        )}

        <Button
          type="submit"
          className="w-full py-3 text-base font-semibold"
          loading={loading}
        >
          Iniciar sesión
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </form>

      <div className="mt-6 text-center space-y-3">
        <Link
          to="/forgot-password"
          className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
        >
          ¿Olvidaste tu contraseña?
        </Link>
        <p className="text-sm text-gray-500">
          ¿No tienes cuenta?{" "}
          <Link
            to={
              redirectParam
                ? `/register?redirect=${encodeURIComponent(redirectParam)}`
                : "/register"
            }
            className="text-primary-600 hover:text-primary-700 font-semibold transition-colors duration-200"
          >
            Regístrate aquí
          </Link>
        </p>
      </div>
    </div>
  );
}
